import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import WidgetRenderer from '../components/WidgetRenderer';
import WaveLoader from '../components/WaveLoader';

/**
 * Custom Widgets page — gallery of saved widgets + AI builder (admin only).
 *
 * Props:
 *   currentUser - { organization, username, role }
 *   isStaging   - boolean
 */
function CustomWidgets({ currentUser }) {
  const [view, setView] = useState('gallery'); // 'gallery' | 'builder'
  const [widgets, setWidgets] = useState([]);
  const [widgetData, setWidgetData] = useState({});
  const [loading, setLoading] = useState(true);

  // Builder state
  const [prompt, setPrompt] = useState('');
  const [targetPage, setTargetPage] = useState('custom');
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [modules, setModules] = useState([]);

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'admin_dachido';
  const authHeaders = {
    'X-Organization': currentUser?.organization || '',
    'X-Username': currentUser?.username || '',
  };

  // Fetch available modules for the page target dropdown, filtered to the
  // tabs actually enabled for the current organization.
  useEffect(() => {
    Promise.all([
      axios.get('/api/config/modules'),
      axios.get('/api/config/org-tabs', {
        params: { organization: currentUser?.organization || '' },
      }),
    ])
      .then(([modsRes, tabsRes]) => {
        const allModules = modsRes.data.modules || modsRes.data || [];
        const orgTabs = tabsRes.data?.tabs || [];
        const enabled = new Set(orgTabs);
        setModules(allModules.filter(m => m.id !== 'custom-widgets' && enabled.has(m.id)));
      })
      .catch(() => {});
  }, [currentUser?.organization]);

  const fetchWidgets = useCallback(() => {
    setLoading(true);
    axios.get('/api/widgets?page_id=custom', { headers: authHeaders })
      .then(res => {
        const list = res.data.widgets || [];
        setWidgets(list);
        return Promise.all(
          list.map(w =>
            axios.get(`/api/widgets/${w.id}/data`, { headers: authHeaders })
              .then(r => ({ id: w.id, data: r.data.data, viz_config: r.data.viz_config }))
              .catch(() => ({ id: w.id, data: null, viz_config: null }))
          )
        );
      })
      .then(results => {
        const dataMap = {};
        (results || []).forEach(r => { dataMap[r.id] = r; });
        setWidgetData(dataMap);
      })
      .catch(err => console.error('Error loading widgets:', err))
      .finally(() => setLoading(false));
  }, [currentUser?.organization, currentUser?.username]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchWidgets(); }, [fetchWidgets]);

  // Generate widget via LLM
  const handleGenerate = () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError('');
    setPreview(null);

    axios.post('/api/widgets/generate', { prompt: prompt.trim() }, { headers: authHeaders })
      .then(res => {
        if (res.data.success) {
          setPreview(res.data.data);
        } else {
          setError(res.data.error || 'Generation failed');
        }
      })
      .catch(err => {
        const msg = err.response?.data?.error || err.message || 'Generation failed';
        setError(msg);
      })
      .finally(() => setGenerating(false));
  };

  // Save widget
  const handleSave = () => {
    if (!preview) return;
    axios.post('/api/widgets/save', {
      python_code: preview.python_code,
      viz_config: preview.viz_config,
      title: preview.title,
      description: preview.description,
      prompt_text: prompt.trim(),
      page_id: targetPage,
    }, { headers: authHeaders })
      .then(res => {
        if (res.data.success) {
          setView('gallery');
          setPreview(null);
          setPrompt('');
          setTargetPage('custom');
          fetchWidgets();
        } else {
          setError(res.data.error || 'Save failed');
        }
      })
      .catch(err => setError(err.response?.data?.error || 'Save failed'));
  };

  // Delete widget
  const handleDelete = (widgetId) => {
    if (!window.confirm('Delete this widget?')) return;
    axios.delete(`/api/widgets/${widgetId}`, { headers: authHeaders })
      .then(() => fetchWidgets())
      .catch(err => console.error('Delete failed:', err));
  };

  // ---- Render ----

  const btnStyle = {
    padding: '10px 18px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
    background: '#1e3a5f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  };

  const btnSecondary = {
    ...btnStyle,
    background: '#6c757d',
  };

  const btnDanger = {
    ...btnStyle,
    background: '#dc3545',
    padding: '6px 12px',
    fontSize: '12px',
  };

  if (view === 'builder') {
    return (
      <div>
        <div className="module-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h2>Create Widget</h2>
              <p>Describe the data you want to visualize in plain language.</p>
            </div>
            <button type="button" style={btnSecondary} onClick={() => { setView('gallery'); setPreview(null); setError(''); }}>
              Back to Gallery
            </button>
          </div>
        </div>

        {/* Prompt input */}
        <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}>
            What would you like to see?
          </label>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="e.g., Show me the number of conversations per region for the last 30 days as a bar chart"
            rows={3}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, marginRight: '8px' }}>Place on page:</label>
              <select
                value={targetPage}
                onChange={e => setTargetPage(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}
              >
                <option value="custom">Custom (this page)</option>
                {modules.map(m => (
                  <option key={m.id} value={m.id}>{m.label} ({m.id})</option>
                ))}
              </select>
            </div>

            <button
              type="button"
              style={{ ...btnStyle, cursor: generating ? 'wait' : 'pointer', opacity: generating ? 0.7 : 1 }}
              onClick={handleGenerate}
              disabled={generating || !prompt.trim()}
            >
              {generating ? 'Generating...' : 'Generate Widget'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '12px 16px',
            background: '#ffeef0',
            border: '1px solid #dc3545',
            borderRadius: '8px',
            color: '#dc3545',
            marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div className="card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0 }}>{preview.title}</h3>
                {preview.description && <p style={{ margin: '4px 0 0', color: '#6c757d', fontSize: '14px' }}>{preview.description}</p>}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" style={btnStyle} onClick={handleSave}>
                  Save Widget
                </button>
                <button type="button" style={btnSecondary} onClick={() => { setPreview(null); setError(''); }}>
                  Try Again
                </button>
              </div>
            </div>
            <WidgetRenderer data={preview.result_data} vizConfig={preview.viz_config} />
          </div>
        )}

        {/* Loading indicator */}
        {generating && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6c757d' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>...</div>
            <p>Analyzing your request and generating a widget...</p>
            <p style={{ fontSize: '12px' }}>This may take 10-20 seconds</p>
          </div>
        )}
      </div>
    );
  }

  // Gallery view
  return (
    <div>
      <div className="module-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2>Custom Widgets</h2>
            <p>AI-generated data visualizations for your organization.</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              type="button"
              style={{ ...btnStyle, cursor: loading ? 'wait' : 'pointer' }}
              onClick={fetchWidgets}
              disabled={loading}
            >
              <span aria-hidden>&#8635;</span> {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            {isAdmin && (
              <button type="button" style={btnStyle} onClick={() => setView('builder')}>
                + Create Widget
              </button>
            )}
          </div>
        </div>
      </div>

      {loading && !widgets.length && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6c757d' }}>Loading widgets...</div>
      )}

      {!loading && !widgets.length && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6c757d' }}>
          <p style={{ fontSize: '18px', marginBottom: '8px' }}>No custom widgets yet</p>
          {isAdmin && <p>Click "Create Widget" to build your first one using AI.</p>}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))',
        gap: '24px',
      }}>
        {widgets.map(w => {
          const wd = widgetData[w.id];
          return (
            <div key={w.id} className="card" style={{ padding: '16px', position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <h4 style={{ margin: 0 }}>{w.title}</h4>
                  {w.description && <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6c757d' }}>{w.description}</p>}
                </div>
                {isAdmin && (
                  <button type="button" style={btnDanger} onClick={() => handleDelete(w.id)}>
                    Delete
                  </button>
                )}
              </div>
              {wd && wd.data ? (
                <WidgetRenderer data={wd.data} vizConfig={wd.viz_config} />
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                  {loading ? <WaveLoader size="small" /> : 'Failed to load data'}
                </div>
              )}
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#aaa' }}>
                Created by {w.created_by} on {w.created_at ? new Date(w.created_at).toLocaleDateString() : '—'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CustomWidgets;
